import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, Trophy, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AppSurface } from "@/components/app/AppSurface";
import { AppLogo } from "@/components/app/brand/AppLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { cn } from "@/lib/utils";
import authBg from "@/assets/auth_background/fanjago_auth_bg.jpeg";

function useMinWidth(px: number) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${px}px)`);
    const apply = () => setMatches(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, [px]);
  return matches;
}

function LocaleToggle() {
  const { t, i18n } = useTranslation();
  const current = i18n.resolvedLanguage?.startsWith("ms") ? "ms" : "en";
  return (
    <div role="group" aria-label={t("authUi.language")} className="inline-flex rounded-md border border-border p-0.5">
      {(["en", "ms"] as const).map((code) => (
        <button
          key={code}
          type="button"
          aria-pressed={current === code}
          onClick={() => i18n.changeLanguage(code)}
          className={cn(
            "h-11 min-w-11 rounded px-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            current === code ? "bg-primary text-primary-foreground" : "text-foreground",
          )}
        >
          {code === "en" ? "EN" : "BM"}
        </button>
      ))}
    </div>
  );
}

function BrandPanel({ band }: { band: boolean }) {
  const { t } = useTranslation();
  const bullets = [
    { icon: Users, label: t("authUi.bulletTeam") },
    { icon: Target, label: t("authUi.bulletTrack") },
    { icon: Trophy, label: t("authUi.bulletWin") },
  ];
  return (
    <aside
      className={cn(
        "relative overflow-hidden text-[#F9FAFB]",
        band ? "h-40 md:block lg:hidden" : "hidden lg:col-span-5 lg:block lg:min-h-dvh",
      )}
    >
      <img src={authBg} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#0F1115]/[0.56]" />
      <svg className="absolute inset-0 h-full w-full opacity-[0.08]" viewBox="0 0 400 800" aria-hidden>
        <path d="M20 40 H380 M20 200 H380 M20 360 H380 M200 40 V760" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="200" cy="400" r="48" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
      <div className={cn("relative flex h-full flex-col justify-between p-6", band && "p-4")}>
        <AppLogo variant="wordmark" size={band ? 32 : 48} surface="dark" />
        {!band && (
          <div className="max-w-sm space-y-6">
            <h2 className="font-heading text-[2.5rem] font-bold leading-[1.1]">{t("authUi.champion")}</h2>
            <div className="h-[3px] w-16 bg-primary" />
            <ul className="space-y-3 text-sm">
              {bullets.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3">
                  <Icon className="h-5 w-5 shrink-0" aria-hidden />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <span className="sr-only">FanJago</span>
      </div>
    </aside>
  );
}

export function AuthLayout({
  children,
  width = "form",
}: {
  children: ReactNode;
  width?: "form" | "wide";
}) {
  const { t } = useTranslation();
  const showPhoto = useMinWidth(768);
  return (
    <AppSurface>
      <div className="min-h-dvh bg-background text-foreground">
        <div className="lg:grid lg:min-h-dvh lg:grid-cols-12">
          {showPhoto && <BrandPanel band={false} />}
          <div className="flex min-h-dvh flex-col lg:col-span-7">
            {showPhoto && <BrandPanel band />}
            <div className="flex items-center justify-between gap-3 px-4 pt-[max(0.75rem,env(safe-area-inset-top))]">
              <Link to="/" className="text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {t("authUi.home")}
              </Link>
              <div className="flex items-center gap-2">
                <LocaleToggle />
                <ThemeToggle variant="icon" />
              </div>
            </div>
            {!showPhoto && (
              <div className="flex justify-center px-4 py-4">
                <AppLogo variant="wordmark" size={40} title="FanJago" />
              </div>
            )}
            <div className="flex flex-1 justify-center px-4 pb-[max(2rem,env(safe-area-inset-bottom))]">
              <div className={cn("w-full py-6 lg:py-10", width === "wide" ? "max-w-[880px]" : "max-w-[400px] lg:my-auto")}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppSurface>
  );
}
